import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Key, CheckCircle, XCircle, Clock, ChevronRight, Plus, MapPin, Calendar, Play } from 'lucide-react'
import { useAuthStore } from '../../stores/authStore'
import api from '../../services/api'

const BLUE   = '#0A84FF'
const TEAL   = '#00D4AA'
const ORANGE = '#FF9500'
const RED    = '#FF3B5C'
const DIM    = 'rgba(255,255,255,0.3)'

const STATUS_META: Record<string, { label: string; color: string }> = {
  draft:            { label: 'Draft',            color: DIM },
  pending_approval: { label: 'Pending Approval', color: ORANGE },
  approved:         { label: 'Approved',         color: BLUE },
  active:           { label: 'Active',           color: TEAL },
  closed:           { label: 'Closed',           color: DIM },
  cancelled:        { label: 'Cancelled',        color: RED },
}

const TYPE_ICONS: Record<string, string> = {
  hot_work:            '🔥',
  confined_space:      '🕳️',
  electrical_isolation:'⚡',
  heights:             '🏗️',
  excavation:          '⛏️',
  general:             '📋',
}

const FILTERS = [
  ['active',           'Active'],
  ['pending_approval', 'Pending'],
  ['approved',         'Approved'],
  ['all',              'All'],
]

const CAN_APPROVE_ROLES = ['tenant_admin', 'corp_safety', 'ops_safety', 'supervisor']
const CAN_SIGN_ON_ROLES = ['tenant_admin', 'corp_safety', 'ops_safety', 'supervisor']

export default function MobilePermits() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [permits, setPermits] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('active')
  const [acting, setActing] = useState<string | null>(null)

  const load = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams({ limit: '50' })
      if (filter !== 'all') params.set('status', filter)
      const { data } = await api.get(`/permits?${params}`)
      setPermits(data.data || [])
      setTotal(data.total || 0)
    } catch {}
    setLoading(false)
  }

  useEffect(() => { load() }, [filter])

  const canApprove = CAN_APPROVE_ROLES.includes(user?.role || '')
  const canSignOn  = CAN_SIGN_ON_ROLES.includes(user?.role || '')

  const doApprove = async (id: string) => {
    setActing(id)
    try {
      await api.post(`/permits/${id}/approve`)
      await load()
    } catch {}
    setActing(null)
  }

  const doSignOn = async (id: string) => {
    setActing(id)
    try {
      await api.post(`/permits/${id}/sign-on`)
      await load()
    } catch {}
    setActing(null)
  }

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100%', paddingBottom: 96 }}>
      {/* Header */}
      <div className="sticky top-0 z-10" style={{ background: 'var(--bg)', borderBottom: '1px solid rgba(255,255,255,0.06)', padding: '16px 20px 12px' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 12 }}>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: '#FFFFFF', margin: 0 }}>Permits</h1>
          <span style={{ fontSize: 12, fontWeight: 500, color: DIM }}>{total} total</span>
        </div>
        <div style={{ display: 'flex', gap: 8, overflowX: 'auto', scrollbarWidth: 'none' }}>
          {FILTERS.map(([val, label]) => {
            const active = filter === val
            return (
              <button key={val} onClick={() => setFilter(val)} style={{
                padding: '6px 14px', borderRadius: 999, fontSize: 12, fontWeight: 600, flexShrink: 0,
                border: 'none', cursor: 'pointer',
                background: active ? BLUE : 'rgba(255,255,255,0.08)',
                color: active ? '#FFFFFF' : 'rgba(255,255,255,0.45)',
                transition: 'all 150ms',
              }}>
                {label}
              </button>
            )
          })}
        </div>
      </div>

      {/* List */}
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: '64px 0', color: DIM }}>Loading…</div>
        ) : permits.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 0', gap: 12 }}>
            <div style={{ width: 60, height: 60, borderRadius: '50%', background: `${BLUE}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Key size={26} color={BLUE} />
            </div>
            <p style={{ fontWeight: 600, fontSize: 15, color: '#FFFFFF', margin: 0 }}>No permits</p>
            <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.35)', margin: 0 }}>
              {filter === 'all' ? 'No permits recorded' : `No ${filter.replace('_', ' ')} permits`}
            </p>
            <button onClick={() => navigate('/m/permits/new')} style={{
              marginTop: 4, padding: '10px 20px', borderRadius: 999, fontSize: 13, fontWeight: 600,
              color: '#FFFFFF', background: BLUE, border: 'none', cursor: 'pointer',
            }}>
              New Permit
            </button>
          </div>
        ) : permits.map(permit => {
          const meta = STATUS_META[permit.status] || STATUS_META.draft
          const icon = TYPE_ICONS[permit.permit_type] || '📋'
          const isPending  = permit.status === 'pending_approval'
          const isApproved = permit.status === 'approved'
          const isActing   = acting === permit.id

          return (
            <div key={permit.id} style={{ background: 'var(--white)', borderRadius: 18, overflow: 'hidden', borderLeft: `3px solid ${meta.color}` }}>
              <div style={{ padding: '14px 14px 12px 16px' }}>
                {/* Top row */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 22 }}>{icon}</span>
                    <div>
                      <span style={{ fontSize: 10, fontFamily: 'monospace', fontWeight: 600, color: 'rgba(255,255,255,0.4)' }}>
                        {permit.permit_number}
                      </span>
                      <span style={{ marginLeft: 8, fontSize: 11, padding: '2px 8px', borderRadius: 999, fontWeight: 600, background: `${meta.color}20`, color: meta.color }}>
                        {meta.label}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Work description */}
                <p style={{ fontSize: 13, fontWeight: 600, color: '#FFFFFF', margin: '0 0 8px', lineHeight: 1.4 }}>
                  {(permit.work_description || '').slice(0, 80)}{permit.work_description?.length > 80 ? '…' : ''}
                </p>

                {/* Meta row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, fontSize: 11, color: 'rgba(255,255,255,0.3)', flexWrap: 'wrap' }}>
                  {permit.site_name && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <MapPin size={10} />{permit.site_name}
                    </span>
                  )}
                  {permit.valid_to && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                      <Clock size={10} />Expires {new Date(permit.valid_to).toLocaleString('en-AU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                    </span>
                  )}
                  {permit.issuer_name && (
                    <span>Issued by {permit.issuer_name}</span>
                  )}
                </div>
              </div>

              {/* Action row */}
              <div style={{ display: 'flex', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                {isPending && canApprove && (
                  <>
                    <button
                      onClick={() => doApprove(permit.id)}
                      disabled={!!isActing}
                      style={{ flex: 1, padding: '11px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13, fontWeight: 700, border: 'none', background: 'none', color: TEAL, cursor: isActing ? 'default' : 'pointer', opacity: isActing ? 0.5 : 1 }}
                      className="active:opacity-75 transition-opacity"
                    >
                      <CheckCircle size={14} /> {isActing ? 'Approving…' : 'Approve'}
                    </button>
                    <div style={{ width: 1, background: 'rgba(255,255,255,0.06)' }} />
                    <button
                      onClick={() => navigate(`/m/permits/${permit.id}`)}
                      style={{ flex: 1, padding: '11px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13, fontWeight: 500, border: 'none', background: 'none', color: DIM, cursor: 'pointer' }}
                      className="active:opacity-75 transition-opacity"
                    >
                      View <ChevronRight size={13} />
                    </button>
                  </>
                )}
                {isApproved && canSignOn && (
                  <>
                    <button
                      onClick={() => doSignOn(permit.id)}
                      disabled={!!isActing}
                      style={{ flex: 1, padding: '11px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13, fontWeight: 700, border: 'none', background: 'none', color: ORANGE, cursor: isActing ? 'default' : 'pointer', opacity: isActing ? 0.5 : 1 }}
                      className="active:opacity-75 transition-opacity"
                    >
                      <Play size={13} fill={ORANGE} color={ORANGE} /> {isActing ? 'Starting…' : 'Sign On & Start'}
                    </button>
                    <div style={{ width: 1, background: 'rgba(255,255,255,0.06)' }} />
                    <button
                      onClick={() => navigate(`/m/permits/${permit.id}`)}
                      style={{ flex: 1, padding: '11px 0', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13, fontWeight: 500, border: 'none', background: 'none', color: DIM, cursor: 'pointer' }}
                      className="active:opacity-75 transition-opacity"
                    >
                      View <ChevronRight size={13} />
                    </button>
                  </>
                )}
                {!isPending && !isApproved && (
                  <button
                    onClick={() => navigate(`/m/permits/${permit.id}`)}
                    style={{ flex: 1, padding: '11px 14px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7, fontSize: 13, fontWeight: 500, border: 'none', background: 'none', color: DIM, cursor: 'pointer' }}
                    className="active:opacity-75 transition-opacity"
                  >
                    View details <ChevronRight size={13} />
                  </button>
                )}
              </div>
            </div>
          )
        })}
      </div>

      {/* FAB */}
      <button
        onClick={() => navigate('/m/permits/new')}
        style={{
          position: 'fixed',
          bottom: 'calc(64px + env(safe-area-inset-bottom) + 16px)',
          right: 20, width: 52, height: 52, borderRadius: '50%',
          background: BLUE, boxShadow: `0 0 24px ${BLUE}55`,
          border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', zIndex: 50,
        }}
        className="active:scale-95 transition-transform"
      >
        <Plus size={24} color="white" />
      </button>
    </div>
  )
}
